import React, { useState, useEffect, useCallback } from 'react';
import { leads as leadsApi } from '../../lib/api';
import { getIntlLocale } from '../../i18n/runtime';
import {
  Search,
  Plus,
  Upload,
  Link2,
  Trash2,
  Loader2,
  Users,
  FileText,
  X,
  Check,
  ExternalLink,
  Calendar,
  LayoutList,
  LayoutGrid,
  MoreHorizontal,
} from 'lucide-react';

const PIPELINE = [
  { key: 'contacted', label: 'Contacted', color: 'border-t-[#B35900]' },
  { key: 'not_interested', label: 'Not interested', color: 'border-t-[#CE3636]' },
  { key: 'booked_meeting', label: 'Booked meeting', color: 'border-t-[#0052CC]' },
  { key: 'waiting_confirmation', label: 'Waiting for confirmation', color: 'border-t-[#6B00B3]' },
  { key: 'onboarded', label: 'Onboarded', color: 'border-t-[#1E7F4E]' },
];

const STATUS_LABELS = Object.fromEntries(PIPELINE.map((c) => [c.key, c.label]));
// Back-compat
STATUS_LABELS.new = 'New';
STATUS_LABELS.replied = 'Replied';
STATUS_LABELS.converted = 'Onboarded';

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('list'); // 'list' | 'pipeline'
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState('urls');
  const [urlInput, setUrlInput] = useState('');
  const [importing, setImporting] = useState(false);
  const [autoOutreach, setAutoOutreach] = useState(false);
  const [bookingModal, setBookingModal] = useState(null); // lead obj
  const [actionsFor, setActionsFor] = useState(null); // lead id whose dropdown is open
  const fileInputRef = React.useRef(null);

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const data = await leadsApi.list({ search, limit: 500 });
      setLeads(data.leads);
    } catch (err) {
      console.error('Failed to load leads:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    function onDoc(e) {
      // Don't close if the click is inside the actions menu or its trigger button
      if (e.target.closest('[data-actions-menu]') || e.target.closest('[data-actions-trigger]')) {
        return;
      }
      setActionsFor(null);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const handleImportUrls = async () => {
    const urls = urlInput.split('\n').map((u) => u.trim()).filter((u) => u.length > 0);
    if (urls.length === 0) return;
    try {
      setImporting(true);
      await leadsApi.importUrls(urls, autoOutreach);
      setShowImportModal(false);
      setUrlInput('');
      setTimeout(loadLeads, 1500);
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setImporting(true);
      await leadsApi.importCsv(file, autoOutreach);
      setShowImportModal(false);
      setTimeout(loadLeads, 1500);
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Delete this lead?')) return;
    await leadsApi.delete(leadId);
    setLeads((prev) => prev.filter((l) => l.id !== leadId));
  };

  const updateLeadStatus = async (leadId, status) => {
    const updated = await leadsApi.patch(leadId, { status });
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, ...updated } : l)));
  };

  const openBooking = (lead) => {
    setBookingModal(lead);
    setActionsFor(null);
  };

  const confirmBooking = async (lead, notes) => {
    await leadsApi.patch(lead.id, { status: 'booked_meeting', notes });
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: 'booked_meeting', notes } : l)));
    setBookingModal(null);
  };

  const total = leads.length;

  return (
    <div className="app-page-shell bg-white" data-testid="leads-page">
      <header className="h-14 border-b border-[#E3E2E0] flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold text-[#191919]">Leads</h1>
          <span className="text-xs text-[#6B6B6B]">{total} total</span>
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex items-center gap-0.5 bg-[#F0F0EF] rounded-lg p-0.5" data-testid="leads-view-toggle">
            <button
              onClick={() => setView('list')}
              className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md ${view === 'list' ? 'bg-white text-[#191919] shadow-sm' : 'text-[#6B6B6B]'}`}
              data-testid="view-list"
            >
              <LayoutList className="w-3.5 h-3.5" /> List
            </button>
            <button
              onClick={() => setView('pipeline')}
              className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md ${view === 'pipeline' ? 'bg-white text-[#191919] shadow-sm' : 'text-[#6B6B6B]'}`}
              data-testid="view-pipeline"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Pipeline
            </button>
          </div>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-3.5 py-1.5 bg-[#0A0A0A] hover:bg-[#2D2D2D] text-white text-sm font-medium rounded-lg"
            data-testid="import-leads-btn"
          >
            <Plus className="w-4 h-4" />
            Import
          </button>
        </div>
      </header>

      {/* Search */}
      <div className="px-6 py-3 border-b border-[#E3E2E0] shrink-0">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B9B9B]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search leads..."
            className="w-full pl-9 pr-3 py-1.5 bg-white border border-[#E3E2E0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/10"
            data-testid="leads-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="w-6 h-6 animate-spin text-[#6B6B6B]" />
        </div>
      ) : leads.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#F7F7F5] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-7 h-7 text-[#6B6B6B]" />
            </div>
            <h3 className="text-base font-medium text-[#191919]">No leads yet</h3>
            <p className="text-sm text-[#6B6B6B] mt-1">Import leads from URLs, CSV, or from the Inbox.</p>
            <button onClick={() => setShowImportModal(true)} className="mt-5 px-5 py-2 bg-[#0A0A0A] hover:bg-[#2D2D2D] text-white text-sm font-medium rounded-lg">
              Import Leads
            </button>
          </div>
        </div>
      ) : view === 'list' ? (
        <ListView
          leads={leads}
          onDelete={handleDelete}
          onStatusChange={updateLeadStatus}
          onBook={openBooking}
          actionsFor={actionsFor}
          setActionsFor={setActionsFor}
        />
      ) : (
        <PipelineView
          leads={leads}
          onStatusChange={updateLeadStatus}
          onBook={openBooking}
          onDelete={handleDelete}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-xl">
            <div className="flex items-center justify-between p-6 border-b border-[#E3E2E0]">
              <h2 className="text-lg font-semibold text-[#191919]">Import Leads</h2>
              <button onClick={() => setShowImportModal(false)} className="p-2 hover:bg-[#F0F0EF] rounded-lg">
                <X className="w-5 h-5 text-[#6B6B6B]" />
              </button>
            </div>
            <div className="p-6">
              <div className="flex gap-2 mb-6">
                <button onClick={() => setImportType('urls')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm ${importType === 'urls' ? 'bg-[#0A0A0A] text-white border-transparent' : 'bg-white text-[#6B6B6B] border-[#E3E2E0]'}`}>
                  <Link2 className="w-4 h-4" /> LinkedIn URLs
                </button>
                <button onClick={() => setImportType('csv')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm ${importType === 'csv' ? 'bg-[#0A0A0A] text-white border-transparent' : 'bg-white text-[#6B6B6B] border-[#E3E2E0]'}`}>
                  <FileText className="w-4 h-4" /> CSV File
                </button>
              </div>
              {importType === 'urls' ? (
                <textarea
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder={'https://linkedin.com/in/johndoe\nhttps://linkedin.com/in/janedoe'}
                  rows={6}
                  className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm resize-none"
                  data-testid="url-input"
                />
              ) : (
                <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-[#E3E2E0] rounded-lg p-8 text-center cursor-pointer hover:border-[#0A0A0A]">
                  <Upload className="w-8 h-8 text-[#9B9B9B] mx-auto mb-3" />
                  <p className="text-sm text-[#6B6B6B]">Click to upload CSV</p>
                  <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileUpload} className="hidden" data-testid="csv-input" />
                </div>
              )}
              <div className="flex items-center justify-between mt-5 p-3 bg-[#FBFBFA] rounded-lg">
                <div>
                  <p className="text-sm font-medium text-[#191919]">Auto-outreach</p>
                  <p className="text-xs text-[#6B6B6B]">Queue these leads for automated outreach.</p>
                </div>
                <button onClick={() => setAutoOutreach(!autoOutreach)} className={`w-10 h-6 rounded-full ${autoOutreach ? 'bg-[#0A0A0A]' : 'bg-[#E3E2E0]'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full shadow ${autoOutreach ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-[#E3E2E0]">
              <button onClick={() => setShowImportModal(false)} className="px-3 py-2 text-sm text-[#6B6B6B] hover:bg-[#F0F0EF] rounded-lg">
                Cancel
              </button>
              {importType === 'urls' && (
                <button onClick={handleImportUrls} disabled={importing || !urlInput.trim()} className="flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-sm font-medium rounded-lg disabled:opacity-50" data-testid="import-submit-btn">
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Import
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {bookingModal && <BookingModal lead={bookingModal} onClose={() => setBookingModal(null)} onConfirm={confirmBooking} />}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(getIntlLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusBadge({ status }) {
  const label = STATUS_LABELS[status] || status || 'New';
  const styles = {
    contacted: 'bg-[#FFF4E5] text-[#B35900] border-[#F5D9B0]',
    not_interested: 'bg-[#FFEBE9] text-[#CE3636] border-[#FF8080]',
    booked_meeting: 'bg-[#E6EFFF] text-[#0052CC] border-[#C5D8F5]',
    waiting_confirmation: 'bg-[#F0E6FF] text-[#6B00B3] border-[#D9C5F5]',
    onboarded: 'bg-[#E6F4EA] text-[#1E7F4E] border-[#C5E7D2]',
    new: 'bg-[#F0F0EF] text-[#6B6B6B] border-[#E3E2E0]',
    replied: 'bg-[#E6EFFF] text-[#0052CC] border-[#C5D8F5]',
    converted: 'bg-[#E6F4EA] text-[#1E7F4E] border-[#C5E7D2]',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded-md border ${styles[status] || styles.new}`}>
      {label}
    </span>
  );
}

function LeadAvatar({ lead }) {
  if (lead.profile_picture) {
    return <img src={lead.profile_picture} alt={lead.name} className="w-8 h-8 rounded-full object-cover shrink-0" />;
  }
  return (
    <div className="w-8 h-8 rounded-full bg-[#F0F0EF] flex items-center justify-center shrink-0">
      <span className="text-xs font-medium text-[#6B6B6B]">{(lead.name || '?').charAt(0).toUpperCase()}</span>
    </div>
  );
}

function ListView({ leads, onDelete, onStatusChange, onBook, actionsFor, setActionsFor }) {
  return (
    <div className="flex-1 overflow-y-auto overflow-x-hidden" data-testid="leads-list">
      <div className="sticky top-0 bg-[#FBFBFA] border-b border-[#E3E2E0] grid grid-cols-12 gap-3 px-6 py-2 text-[11px] font-semibold text-[#6B6B6B] uppercase tracking-wider z-10">
        <div className="col-span-4">Lead</div>
        <div className="col-span-2">Last contact</div>
        <div className="col-span-2">Location</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      {leads.map((lead) => (
        <div
          key={lead.id}
          className="grid grid-cols-12 gap-3 px-6 py-3 border-b border-[#F0F0EF] hover:bg-[#FBFBFA] text-sm items-center group"
          data-testid={`lead-row-${lead.id}`}
        >
          <div className="col-span-4 flex items-center gap-3 min-w-0">
            <LeadAvatar lead={lead} />
            <div className="min-w-0">
              <p className="font-medium text-[#191919] truncate">{lead.name || 'Unknown'}</p>
              {lead.headline && <p className="text-xs text-[#6B6B6B] truncate">{lead.headline}</p>}
            </div>
          </div>
          <div className="col-span-2 text-[#6B6B6B] text-xs">{formatDate(lead.last_contact_at || lead.created_at)}</div>
          <div className="col-span-2 text-[#6B6B6B] truncate">{lead.location || <span className="text-[#9B9B9B]">—</span>}</div>
          <div className="col-span-2">
            <select
              value={lead.status || 'contacted'}
              onChange={(e) => onStatusChange(lead.id, e.target.value)}
              className="text-[11px] bg-transparent border border-[#E3E2E0] rounded-md px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
              data-testid={`status-select-${lead.id}`}
            >
              {PIPELINE.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2 flex items-center justify-end gap-1 relative">
            {lead.linkedin_url && (
              <a
                href={lead.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 hover:bg-[#F0F0EF] rounded"
                title="Open LinkedIn profile"
                data-testid={`open-profile-${lead.id}`}
              >
                <ExternalLink className="w-3.5 h-3.5 text-[#6B6B6B]" />
              </a>
            )}
            <button
              onClick={() => onBook(lead)}
              className="p-1.5 hover:bg-[#F0F0EF] rounded"
              title="Book a meeting"
              data-testid={`book-meeting-${lead.id}`}
            >
              <Calendar className="w-3.5 h-3.5 text-[#0052CC]" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActionsFor(actionsFor === lead.id ? null : lead.id);
              }}
              className="p-1.5 hover:bg-[#F0F0EF] rounded"
              data-actions-trigger
              data-testid={`actions-btn-${lead.id}`}
            >
              <MoreHorizontal className="w-3.5 h-3.5 text-[#6B6B6B]" />
            </button>
            {actionsFor === lead.id && (
              <div
                className="absolute right-0 top-8 w-44 bg-white border border-[#E3E2E0] rounded-lg shadow-lg z-20 py-1"
                onClick={(e) => e.stopPropagation()}
                data-actions-menu
                data-testid={`actions-menu-${lead.id}`}
              >
                {lead.linkedin_url && (
                  <a
                    href={lead.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[#191919] hover:bg-[#F7F7F5]"
                  >
                    <ExternalLink className="w-4 h-4" /> Open profile
                  </a>
                )}
                <button
                  onClick={() => onBook(lead)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#191919] hover:bg-[#F7F7F5] text-left"
                >
                  <Calendar className="w-4 h-4" /> Book a meeting
                </button>
                <div className="my-1 border-t border-[#F0F0EF]" />
                <button
                  onClick={() => {
                    onDelete(lead.id);
                    setActionsFor(null);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#CE3636] hover:bg-[#FFEBE9] text-left"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function PipelineView({ leads, onStatusChange, onBook, onDelete }) {
  const grouped = PIPELINE.reduce((acc, col) => {
    acc[col.key] = leads.filter((l) => (l.status || 'contacted') === col.key);
    return acc;
  }, {});

  const onDragStart = (e, leadId) => {
    e.dataTransfer.setData('text/plain', leadId);
  };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (e, status) => {
    const id = e.dataTransfer.getData('text/plain');
    if (id) onStatusChange(id, status);
  };

  return (
    <div className="flex-1 overflow-y-auto overflow-x-hidden" data-testid="pipeline-view">
      <div className="flex gap-3 p-3 sm:p-4 min-w-fit snap-x snap-mandatory lg:snap-none">
        {PIPELINE.map((col) => (
          <div
            key={col.key}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, col.key)}
            className={`w-[calc(100vw-32px)] sm:w-64 lg:w-72 shrink-0 snap-start bg-[#FBFBFA] rounded-xl border border-[#E3E2E0] border-t-4 ${col.color}`}
            data-testid={`pipeline-col-${col.key}`}
          >
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#E3E2E0]">
              <h3 className="text-sm font-semibold text-[#191919]">{col.label}</h3>
              <span className="text-xs text-[#6B6B6B] bg-white px-2 py-0.5 rounded-md border border-[#E3E2E0]">
                {grouped[col.key].length}
              </span>
            </div>
            <div className="p-2 space-y-2 max-h-[calc(100vh-220px)] overflow-y-auto">
              {grouped[col.key].length === 0 ? (
                <p className="text-xs text-[#9B9B9B] text-center py-4">Drop leads here</p>
              ) : (
                grouped[col.key].map((lead) => (
                  <div
                    key={lead.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, lead.id)}
                    className="bg-white border border-[#E3E2E0] rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-[#191919]/30 transition-colors"
                    data-testid={`pipeline-card-${lead.id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <LeadAvatar lead={lead} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-[#191919] truncate">{lead.name || 'Unknown'}</p>
                        {lead.headline && <p className="text-xs text-[#6B6B6B] truncate">{lead.headline}</p>}
                        {lead.location && <p className="text-[11px] text-[#9B9B9B] mt-1">{lead.location}</p>}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-[11px] text-[#6B6B6B]">{formatDate(lead.last_contact_at || lead.created_at)}</span>
                      <div className="flex items-center gap-0.5">
                        {lead.linkedin_url && (
                          <a
                            href={lead.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 hover:bg-[#F0F0EF] rounded"
                            title="Open profile"
                          >
                            <ExternalLink className="w-3 h-3 text-[#6B6B6B]" />
                          </a>
                        )}
                        <button onClick={() => onBook(lead)} className="p-1 hover:bg-[#F0F0EF] rounded" title="Book meeting">
                          <Calendar className="w-3 h-3 text-[#0052CC]" />
                        </button>
                        <button onClick={() => onDelete(lead.id)} className="p-1 hover:bg-[#FFEBE9] rounded" title="Delete">
                          <Trash2 className="w-3 h-3 text-[#CE3636]" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BookingModal({ lead, onClose, onConfirm }) {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await onConfirm(lead, notes);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#E3E2E0]">
          <h3 className="text-base font-semibold">Book a meeting with {lead.name || 'lead'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-[#F0F0EF] rounded">
            <X className="w-4 h-4 text-[#6B6B6B]" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-[#6B6B6B]">
            Marking this lead as <strong className="text-[#0052CC]">Booked meeting</strong>. Add optional notes (meeting time, link, etc.).
          </p>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Tue 3pm, Google Meet link sent"
            rows={4}
            className="w-full px-3 py-2 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/10"
            data-testid="booking-notes"
          />
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-[#E3E2E0]">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-[#6B6B6B] hover:bg-[#F0F0EF] rounded-lg">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-3.5 py-1.5 bg-[#0052CC] hover:bg-[#0747A6] text-white text-sm font-medium rounded-lg disabled:opacity-50"
            data-testid="booking-confirm-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm booking'}
          </button>
        </div>
      </div>
    </div>
  );
}
